import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCreateAssignment, useDevices, useScheduleRange, useUsers } from '../api/queries'
import type { Device } from '../api/types'
import { formatDate } from '../lib/format'
import { serviceStatus } from '../pages/DevicesPage'
import { Card, EmptyState } from './ui'

/**
 * Enheder der snart skal serviceres - forsidens overblik for administratorer og
 * projektledere.
 *
 * Listen er der ikke for at blive kigget på, men for at blive planlagt væk:
 * hver linje kan lægges direkte i kalenderen på en medarbejder uden at forlade
 * siden (docs/DECISIONS.md beslutning 028).
 */

/** Hvor langt frem vi kigger. Et kvartal fanger det, der skal planlægges nu. */
const HORIZON_DAYS = 90

export function ServiceDueCard() {
  // Kun dato, ikke klokkeslæt: en ny ISO-streng ved hver render ville give en ny
  // query-nøgle og dermed et uendeligt genhentningsloop.
  const horizon = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() + HORIZON_DAYS)
    return date.toISOString().slice(0, 10)
  }, [])

  const { data, isLoading } = useDevices({ dueBefore: horizon, onServicePlan: true })

  const [planning, setPlanning] = useState<Device | null>(null)

  if (isLoading || !data) {
    return null
  }

  // Allerede planlagte servicebesøg skal ikke ligge og larme på forsiden.
  const devices = data

  if (devices.length === 0) {
    return (
      <Card title="Service forfalder snart">
        <EmptyState>Ingen enheder skal serviceres inden for de næste tre måneder.</EmptyState>
      </Card>
    )
  }

  const overdue = devices.filter((d) => (serviceStatus(d)?.days ?? 1) < 0)

  return (
    <Card
      title={`Service forfalder snart (${devices.length})`}
      actions={
        <Link className="btn btn--sm" to="/devices">
          Alle enheder
        </Link>
      }
    >
      {overdue.length > 0 && (
        <div className="small" style={{ marginBottom: 10, color: '#991B1B' }}>
          <strong>
            {overdue.length} {overdue.length === 1 ? 'enhed er' : 'enheder er'} overskredet.
          </strong>
        </div>
      )}

      <div className="stack">
        {devices.map((device) => (
          <ServiceDueRow
            key={device.id}
            device={device}
            planning={planning?.id === device.id}
            onPlan={() => setPlanning(planning?.id === device.id ? null : device)}
            onDone={() => setPlanning(null)}
          />
        ))}
      </div>
    </Card>
  )
}

function ServiceDueRow({
  device,
  planning,
  onPlan,
  onDone
}: {
  device: Device
  planning: boolean
  onPlan: () => void
  onDone: () => void
}) {
  const due = serviceStatus(device)

  return (
    <div className="device-row" style={{ display: 'block' }}>
      <div className="row row--between" style={{ gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <Link className="small" to={`/devices/${device.id}`} style={{ fontWeight: 600 }}>
            {device.name}
          </Link>
          <div className="tiny muted">
            {[device.customerName, device.location].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div className="row" style={{ gap: 8, whiteSpace: 'nowrap' }}>
          <div style={{ textAlign: 'right' }}>
            {due && (
              <span className="pill" style={{ background: due.background, color: due.color }}>
                {due.label}
              </span>
            )}
            <div className="tiny muted">{formatDate(device.nextServiceDate)}</div>
          </div>
          <button className="btn btn--sm" onClick={onPlan}>
            {planning ? 'Luk' : 'Planlæg'}
          </button>
        </div>
      </div>

      {planning && <PlanServiceForm device={device} onDone={onDone} />}
    </div>
  )
}

/** Læg et servicebesøg i kalenderen på en medarbejder. */
function PlanServiceForm({ device, onDone }: { device: Device; onDone: () => void }) {
  const { data: users } = useUsers()
  const create = useCreateAssignment()

  const [userId, setUserId] = useState('')
  const [date, setDate] = useState(defaultDate(device.nextServiceDate))
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Vis hvad medarbejderen allerede har den dag, så man ikke dobbeltbooker.
  const { data: sameDay } = useScheduleRange(date, date)
  const busy = (sameDay ?? []).filter((a) => String(a.user.id) === userId)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      await create.mutateAsync({
        userId: Number(userId),
        deviceId: device.id,
        date,
        note: note || `Service: ${device.name}`
      })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Servicebesøget kunne ikke planlægges.')
    }
  }

  return (
    <form className="plan-service" onSubmit={submit}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <select
          required
          style={{ maxWidth: 200 }}
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        >
          <option value="">Vælg medarbejder</option>
          {users?.map((user) => (
            <option key={user.id} value={user.id}>
              {user.displayName}
            </option>
          ))}
        </select>

        <input
          type="date"
          required
          style={{ maxWidth: 170 }}
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />

        <input
          style={{ flex: 1, minWidth: 160 }}
          placeholder={`Service: ${device.name}`}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />

        <button className="btn btn--primary btn--sm" type="submit" disabled={!userId || create.isPending}>
          {create.isPending ? 'Lægger i kalender ...' : 'Læg i kalender'}
        </button>
      </div>

      {userId && busy.length > 0 && (
        <div className="tiny muted" style={{ marginTop: 6 }}>
          Har allerede {busy.length} post{busy.length === 1 ? '' : 'er'} den dag:{' '}
          {busy.map((a) => a.projectName).join(', ')}
        </div>
      )}

      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}
    </form>
  )
}

/**
 * Foreslå forfaldsdatoen - eller i dag, hvis den er overskredet.
 * En dato i fortiden i kalenderen hjælper ingen.
 */
function defaultDate(nextService?: string | null) {
  const today = new Date()
  const due = nextService ? new Date(nextService) : today

  return (due > today ? due : today).toISOString().slice(0, 10)
}
